
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { UploadFile } from '@/shared/models/UploadFile';
import { required, email, sameAs } from '@vuelidate/validators';
import { estateAgentService } from '@/estate-agent/EstateAgentService';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import { mapState } from 'vuex';
import { GlobalState } from '@/shared/models/GlobalState';
import useVuelidate from '@vuelidate/core';
import PhonePrefix from '@/shared/components/PhonePrefix.vue';
import config from '@/shared/constants/config';
import phoneNumber from '@/shared/helpers/phoneNumberValidator';
import Cropper from '@/shared/components/Cropper.vue';

export default defineComponent({
  name: 'EstateAgentEdit',
  mixins: [Form],
  emits: ['updated', 'deleted', 'close'],
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    ResizableImage,
    Cropper,
    PhonePrefix,
  },
  props: {
    frontUserId: { type: Number, default: 0 },
  },
  data() {
    return {
      showConfirmDelete: false,
      showConfirmArchive: false,
      estateAgentImage: {} as null | UploadFile,
      showEstateAgentImageModal: false,
      selectedRole: '' as string,
      roles: [],
      model: {},
      form: {
        firstName: null,
        lastName: null,
        email: null,
        confirmEmail: null,
        phoneNumberPrefix: config.DEFAULT_PHONE_PREFIX,
        phoneNumber: null,
        isAdmin: false,
        languageId: (window as never)['languageId'],
        photo: null as null | string,
        photoRelativeUrl: '' as string,
        description: '',
        motto: '',
        languages: [],
      },
      formModel: {},
      isEmailEnabled: false,
    };
  },
  validations() {
    return {
      form: {
        firstName: { required },
        lastName: { required },
        email: { required, email },
        phoneNumberPrefix: { required },
        phoneNumber: { required, phoneNumber: phoneNumber(this.form.phoneNumberPrefix) },
        confirmEmail: { required, sameAsEmail: sameAs(this.form.email) },
        description: {},
        motto: {},
      },
    };
  },
  async created() {
    this.isLoading = true;
    try {
      const result = await estateAgentService.getFormById(this.frontUserId);
      this.formModel = result.data;
      this.model = result.data.model;
      this.form = Object.assign({}, result.data.model);
      this.form.confirmEmail = this.form.email;
      this.selectedRole = this.form.isAdmin.toString().toLowerCase();
      this.roles = result.data.roles;
      this.isEmailEnabled = !this.form.email;

      if (result.data.model.phoneNumberPrefix) {
        this.form.phoneNumberPrefix = config.PHONE_PREFIXES.find((x) => x.value === result.data.model.phoneNumberPrefix);
      }
      if (this.form.photo) {
        this.estateAgentImage = {} as UploadFile;
        this.estateAgentImage.file = this.form.photo;
        this.estateAgentImage.url = this.form.photoRelativeUrl;
        this.estateAgentImage.isSuccess = true;
      }
    } catch (error) {
      console.error(error);
    }
  },
  computed: {
    ...mapState({
      user: (state) => (state as GlobalState).user,
    }),
  },
  methods: {
    async submit() {
      if (!this.validate()) {
        return;
      }

      this.startLoading();

      try {
        this.form.isAdmin = this.selectedRole === 'true';

        const form = Object.assign({}, this.form);

        if (form.phoneNumberPrefix && form.phoneNumberPrefix.value) {
          form.phoneNumberPrefix = form.phoneNumberPrefix.value;
        }

        await estateAgentService.update(form);
        this.updated();
        this.close();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.endLoading();
      }
    },
    async deleteAgent() {
      this.startLoading();

      try {
        await estateAgentService.delete(this.frontUserId);
        this.deleted();
        this.close();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.endLoading();
      }
    },
    async handleEstateAgentImageUpload() {
      const ref = this.$refs.estateAgentImage as any;
      const files = ref.files as string[] | Blob[];
      const file = files[0];
      await this.uploadEstateAgentImage(file);
      ref.value = null;
    },
    async uploadEstateAgentImage(file: any) {
      this.showEstateAgentImageModal = false;
      this.estateAgentImage = {} as UploadFile;
      this.estateAgentImage.isUploading = true;
      this.estateAgentImage.uploadPercentage = 0;
      this.estateAgentImage.file = file.name;
      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const result = await estateAgentService.uploadPhoto(formData, (progressEvent: any) => {
          if (this.estateAgentImage) {
            this.estateAgentImage.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
          }
        });
        this.estateAgentImage.file = result.data.photo;
        this.estateAgentImage.url = result.data.url;
        this.estateAgentImage.isSuccess = true;
        this.form.photo = result.data.photo;
      } catch (error) {
        this.estateAgentImage.isError = true;
        this.estateAgentImage.text = 'Error';
        const errors = this.getApiErrors(error);
        this.estateAgentImage.text = errors[0].message;
      } finally {
        this.estateAgentImage.isUploading = false;
      }
    },
    editEstateAgentFile() {
      this.showEstateAgentImageModal = true;
    },
    deleteEstateAgentFile() {
      this.estateAgentImage = {} as UploadFile;
      this.form.photo = null;
    },
    updated() {
      this.$emit('updated');
    },
    deleted() {
      this.$emit('deleted');
    },
    close() {
      this.$emit('close');
    },
  },
});
